export const INITIAL_PARAMS_STORAGE_KEY = 'tap-connect.initialParams.v1';

export type InitialParamValue = string | string[];
export type InitialParams = Record<string, InitialParamValue>;

type StorageLike = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
};

type HistoryLike = {
  state: unknown;
  replaceState: (
    state: unknown,
    unused: string,
    url?: string | URL | null
  ) => void;
};

type LocationLike = {
  search: string;
  pathname: string;
  hash: string;
};

type BrowserLike = {
  sessionStorage: StorageLike;
  history: HistoryLike;
  location: LocationLike;
};

const canUseBrowser = (browser?: BrowserLike): browser is BrowserLike =>
  !!browser;

const toRecord = (searchParams: URLSearchParams): InitialParams => {
  const record: InitialParams = {};

  for (const key of new Set(searchParams.keys())) {
    const values = searchParams.getAll(key);
    if (values.length === 0) continue;
    record[key.toLowerCase()] = values.length === 1 ? values[0] : values;
  }

  return record;
};

const safeParse = (value: string | null): InitialParams => {
  if (!value) return {};

  try {
    const parsed = JSON.parse(value) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }

    const output: InitialParams = {};
    for (const [key, rawValue] of Object.entries(parsed)) {
      if (typeof rawValue === 'string') {
        output[key] = rawValue;
        continue;
      }

      if (
        Array.isArray(rawValue) &&
        rawValue.every((item) => typeof item === 'string')
      ) {
        output[key] = rawValue;
      }
    }

    return output;
  } catch {
    return {};
  }
};

const getBrowser = (): BrowserLike | undefined => {
  if (typeof window === 'undefined') return undefined;
  return window as unknown as BrowserLike;
};

const write = (params: InitialParams, browser = getBrowser()): void => {
  if (!canUseBrowser(browser)) return;
  browser.sessionStorage.setItem(
    INITIAL_PARAMS_STORAGE_KEY,
    JSON.stringify(params)
  );
};

export const getInitialParams = (browser = getBrowser()): InitialParams => {
  if (!canUseBrowser(browser)) return {};
  return safeParse(browser.sessionStorage.getItem(INITIAL_PARAMS_STORAGE_KEY));
};

export const getInitialParam = (
  key: string,
  browser = getBrowser()
): string | undefined => {
  const value = getInitialParams(browser)[key];
  if (!value) return undefined;
  return Array.isArray(value) ? value[0] : value;
};

export const setInitialParam = (
  key: string,
  value: string | string[] | undefined,
  browser = getBrowser()
): void => {
  if (!canUseBrowser(browser)) return;

  const current = getInitialParams(browser);
  if (value === undefined) {
    delete current[key];
  } else if (Array.isArray(value)) {
    if (value.length === 0) {
      delete current[key];
    } else {
      current[key] = value;
    }
  } else {
    current[key] = value;
  }

  write(current, browser);
};

export const captureInitialParamsFromUrl = (
  browser = getBrowser()
): InitialParams => {
  if (!canUseBrowser(browser)) return {};

  const hasSearch = browser.location.search.length > 1;
  if (!hasSearch) {
    return getInitialParams(browser);
  }

  // Google's OAuth redirect lands here with its own ?code=/?error= query.
  // Don't consume or strip it — GmailConnectPage owns reading and cleaning
  // up that URL itself, and stripping it here (before React even mounts)
  // means the callback page never sees the code.
  const search = new URLSearchParams(browser.location.search);
  if (search.has('code') || search.has('error')) {
    return getInitialParams(browser);
  }

  const parsed = toRecord(search);
  const merged = { ...getInitialParams(browser), ...parsed };
  write(merged, browser);

  const cleanUrl = `${browser.location.pathname}${browser.location.hash}`;
  browser.history.replaceState(browser.history.state, '', cleanUrl);

  return merged;
};
